import React, { useState } from 'react';
import { MapPin, Clock, Phone, Navigation, ChevronRight, Store } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { storeLocations } from '../data/mock';

const LocationsSection = () => {
  const [selectedStore, setSelectedStore] = useState(storeLocations[0]);

  return (
    <section id="locations" className="relative py-20 lg:py-28 bg-gradient-to-b from-[#1e1919] to-[#2d1f2f]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxDYXJpYmJlYW4lMjBiZWFjaHxlbnwwfHx8fDE3Njk1MjM5OTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Caribbean Background"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e1919] via-[#1e1919]/90 to-[#1e1919]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#00d4aa]/10 backdrop-blur-sm border border-[#00d4aa]/30 rounded-full px-4 py-2 mb-6">
            <MapPin className="w-4 h-4 text-[#00d4aa]" />
            <span className="text-[#00d4aa] font-medium text-sm">Visit Us</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Our <span className="text-[#ffd700]">8 Locations</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Pick up and drop off your custom orders at any of our 8 convenient locations across Jamaica.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Store List */}
          <div className="lg:col-span-1">
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-3">
                {storeLocations.map((store) => (
                  <Card
                    key={store.id}
                    onClick={() => setSelectedStore(store)}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedStore?.id === store.id
                        ? 'bg-[#ffd700]/20 border-[#ffd700]'
                        : 'bg-white/5 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedStore?.id === store.id
                              ? 'bg-[#ffd700] text-[#1e1919]'
                              : 'bg-white/10 text-white'
                          }`}>
                            <Store className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className={`font-semibold text-sm ${
                              selectedStore?.id === store.id ? 'text-[#ffd700]' : 'text-white'
                            }`}>
                              {store.name}
                            </h4>
                            <p className="text-white/50 text-xs mt-1">{store.address}</p>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 transition-colors ${
                          selectedStore?.id === store.id ? 'text-[#ffd700]' : 'text-white/30'
                        }`} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Store Details */}
          <div className="lg:col-span-2">
            <Card className="bg-gradient-to-br from-white/10 to-white/5 border-white/10 h-full">
              <CardContent className="p-8">
                {selectedStore && (
                  <div className="h-full flex flex-col">
                    {/* Store Header */}
                    <div className="flex items-start justify-between mb-8">
                      <div>
                        <div className="inline-flex items-center gap-2 bg-[#00d4aa]/20 rounded-full px-3 py-1 mb-3">
                          <div className="w-2 h-2 rounded-full bg-[#00d4aa] animate-pulse" />
                          <span className="text-[#00d4aa] text-xs font-medium">Open Now</span>
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-bold text-white">{selectedStore.name}</h3>
                      </div>
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ffd700]/20 to-[#ff6b6b]/20 flex items-center justify-center">
                        <MapPin className="w-8 h-8 text-[#ffd700]" />
                      </div>
                    </div>

                    {/* Store Info Grid */}
                    <div className="grid sm:grid-cols-2 gap-6 mb-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-[#ff6b6b]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-sm mb-1">Address</p>
                          <p className="text-white font-medium">{selectedStore.address}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-6 h-6 text-[#00d4aa]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-sm mb-1">Business Hours</p>
                          <p className="text-white font-medium">{selectedStore.hours}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-6 h-6 text-[#ffd700]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-sm mb-1">Phone Number</p>
                          <p className="text-white font-medium">{selectedStore.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                          <Navigation className="w-6 h-6 text-[#61525a]" />
                        </div>
                        <div>
                          <p className="text-white/50 text-sm mb-1">Services</p>
                          <p className="text-white font-medium">Pick-up & Drop-off</p>
                        </div>
                      </div>
                    </div>

                    {/* Map Placeholder */}
                    <div className="flex-1 min-h-[200px] rounded-2xl bg-gradient-to-br from-[#00d4aa]/10 to-[#ffd700]/10 border border-white/10 flex items-center justify-center mb-6">
                      <div className="text-center">
                        <MapPin className="w-12 h-12 text-white/30 mx-auto mb-3" />
                        <p className="text-white/50">Interactive map coming soon</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button className="flex-1 bg-gradient-to-r from-[#ffd700] to-[#ffb347] hover:from-[#ffcc00] hover:to-[#ffa500] text-[#1e1919] font-bold">
                        <Navigation className="w-4 h-4 mr-2" />
                        Get Directions
                      </Button>
                      <Button variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Store
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
